import React, { useMemo } from 'react';
import { Box, Radio } from '@mui/material';
import { PathSummaryItem } from '../../../../newMdmReports/utils/hierarchyHelpers';
import { Option } from '../../../../newMdmReports/components/CompactFilterDropdown';
import { DistributorFilter, GeographicalHierarchyFilter, SalesHierarchyFilter } from './FullHerirarchy';
import GeographicalHierarchyNewFilter from './GeograhicalHierarchyFilter';
import { DistributorTypeFilter, DivisionFilter } from './DivisionFilter';
import SalesHierarchyFilterNew from './SalesHierarchyFilterNew';
import DistributorFilterNew from './DistributorFilterNew';
import HierarchyDropdown from './HeirarchyDrowndown';


interface TopFilterBarProps {
  // Config
  geographicalHierarchyFilter?:GeographicalHierarchyFilter;
  salesHierarchyFilter?:SalesHierarchyFilter;
  distributorFilter?:DistributorFilter;
  // State
  filters: Record<string, any>;
  optionsMap: Record<string, Option[]>;
  loadingMap: Record<string, boolean>;
  
  // Base Handlers
  onFilterChange: (key: string, value: any) => void;
  onMultiFilterChange: (key: string, values: string[]) => void;

  // Sales hierarchy handlers
  onSalesLevelChange: (value: string | null) => void;
  onSalesValueChange: (values: string[]) => void;
  onSalesLoadLevels: () => void;
  onSalesNextLevel?: () => void;
  onSalesResetAll?: () => void;
  onSalesDropdownClose?: () => void;

  // Geographical hierarchy handlers
  onGeoLevelChange: (value: string | null) => void;
  onGeoValueChange: (values: string[]) => void;
  onGeoLoadLevels: () => void;
  onGeoNextLevel?: () => void;
  onGeoResetAll?: () => void;
  onGeoDropdownClose?: () => void;
  geoIsLastLevel?: boolean;

  // Selection counts for display (optional)
  salesDrillDownPath?: Array<{ level: string; value: string }>;
  geoDrillDownPath?: Array<{ level: string; value: string }>;
  distributorSource: "geographical" | "sales";
  onDistributorSourceChange: (source: "geographical" | "sales") => void;
  
  // Distributor filter handlers
  onLoadDistributorOptions?: (fieldKey: string) => void;
  shouldDisableDistributorFilter?: boolean;
  distributorDisabledReason?: string;
  searchTextMap?: Record<string, string>;
  onFilterInputChange?: (key: string, inputValue: string) => void;
  
  // Loading state
  isHierarchySyncing?: boolean;
}

// Helper function to summarize drill-down path
const summarizeDrillDownPath = (path: Array<{ level: string; value: string }>): PathSummaryItem[] => {
  const summary: PathSummaryItem[] = [];
  path.forEach((item, index) => {
    const last = summary[summary.length - 1];
    if (last && last.level === item.level) {
      last.count += 1;
    } else {
      summary.push({ 
        level: item.level, 
        count: 1, 
        startIndex: index,
        firstValue: item.value
      });
    }
  });
  return summary;
};

export const DistributorTopFilter: React.FC<TopFilterBarProps> = ({
  distributorFilter,
geographicalHierarchyFilter,
 salesHierarchyFilter,
  filters,
  optionsMap,
  loadingMap,
  onFilterChange,
  onMultiFilterChange,
  onSalesLevelChange,
  onSalesValueChange,
  onSalesLoadLevels,
  onSalesNextLevel,
  onSalesResetAll,
  onSalesDropdownClose,
  onGeoLevelChange,
  onGeoValueChange,
  onGeoLoadLevels,
  onGeoNextLevel,
  onGeoResetAll,
  onGeoDropdownClose,
  geoIsLastLevel,
  salesDrillDownPath = [],
  geoDrillDownPath = [],
  distributorSource,
  onDistributorSourceChange,
  onLoadDistributorOptions,
  shouldDisableDistributorFilter = false,
  distributorDisabledReason,
  searchTextMap = {},
  onFilterInputChange,
  isHierarchySyncing = false
}) => {
  
  // Helper to extract values
  const getFilterValue = (key: string) => {
     return filters[key]?.[0] || null;
  };
  const getFilterValues = (key: string) => {
    return filters[key] || [];
  };
  
  const getOptions = (key: string) => {
    return optionsMap[key] || [];
  };

  const isLoading = (key: string) => {
    return loadingMap[key] || false;
  };

  // Calculate selection counts
  const geoSelectionCount = geoDrillDownPath.length + 
    (geographicalHierarchyFilter?.levelFilterField 
      ? (filters[filters[geographicalHierarchyFilter.levelFilterField]?.[0]]?.length || 0)
      : 0);

  const salesSelectionCount = salesDrillDownPath.length + 
    (salesHierarchyFilter?.levelFilterField 
      ? (filters[filters[salesHierarchyFilter.levelFilterField]?.[0]]?.length || 0)
      : 0);

  // Generate path summaries for display
  const geoPathSummary = useMemo(() => summarizeDrillDownPath(geoDrillDownPath), [geoDrillDownPath]);
  const salesPathSummary = useMemo(() => summarizeDrillDownPath(salesDrillDownPath), [salesDrillDownPath]);

  const geoEnabled = true;
  const salesEnabled = true;
  const showDistributorToggle = geoEnabled && salesEnabled;
  const isGeoActive = geoEnabled && (!showDistributorToggle || distributorSource === "geographical");
  const isSalesActive = salesEnabled && (!showDistributorToggle || distributorSource === "sales");

  const renderSourceRadio = (source: "geographical" | "sales") => {
    if (!showDistributorToggle) return null;
    return (
      <Radio
        size="small"
        checked={distributorSource === source}
        onChange={() => onDistributorSourceChange(source)}
        sx={{
          padding: 0,
          marginRight: "4px",
          "&.Mui-checked": {
            color: "#00A392"
          }
        }}
      />
    );
  };

  return (
    <Box 
      sx={{ 
         width: 'fit-content',
           maxWidth: '100%',
        backgroundColor: '#F3F4F6',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        padding: '4px',
        boxSizing: 'border-box'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '8px',
          rowGap: '14px',
          width: '100%',
          flexWrap: 'nowrap',
          alignItems: 'center'
        }}
      >
        <DistributorTypeFilter
          value={getFilterValues('distributor_type')}
          options={getOptions('distributor_type')}
          onChange={(values) => onMultiFilterChange('distributor_type', values)}
          loading={isLoading('distributor_type')}
          enableSelectAll
          selectAllLabel="Select all types"
        />

        <DivisionFilter
          value={getFilterValues('distributor_division')}
          options={getOptions('distributor_division')}
          onChange={(values) => onMultiFilterChange('distributor_division', values)}
          loading={isLoading('distributor_division')}
          enableSelectAll
          selectAllLabel="Select all divisions"
        />
   
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {renderSourceRadio("geographical")}
            <HierarchyDropdown
              label="Geography" 
              width={325}
              selectionCount={geoSelectionCount}
              drillDownPath={geoDrillDownPath}
              pathSummary={geoPathSummary}
              disabled={!isGeoActive}
              onBeforeClose={onGeoDropdownClose}
              isLoading={isHierarchySyncing}
            >
              <GeographicalHierarchyNewFilter
               geographicalHierarchyFilter={geographicalHierarchyFilter}
                filters={filters}
                optionsMap={optionsMap}
                loadingMap={loadingMap}
                onLevelChange={onGeoLevelChange}
                onValueChange={onGeoValueChange}
                onLoadLevels={onGeoLoadLevels}
                onNextLevel={onGeoNextLevel}
                onResetAll={onGeoResetAll}
                isLastLevel={geoIsLastLevel}
              />
            </HierarchyDropdown>
          </Box>
        

      
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {renderSourceRadio("sales")}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'nowrap' }}>
              <HierarchyDropdown 
                label="Sales Hierarchy" 
                width={325}
                selectionCount={salesSelectionCount}
                drillDownPath={salesDrillDownPath}
                pathSummary={salesPathSummary}
                disabled={!isSalesActive}
                onBeforeClose={onSalesDropdownClose}
                isLoading={isHierarchySyncing}
              >
                <SalesHierarchyFilterNew
              salesHierarchyFilter={salesHierarchyFilter}
                  filters={filters}
                  optionsMap={optionsMap}
                  loadingMap={loadingMap}
                  onLevelChange={onSalesLevelChange}
                  onValueChange={onSalesValueChange}
                  onLoadLevels={onSalesLoadLevels}
                  onNextLevel={onSalesNextLevel}
                  onResetAll={onSalesResetAll}
                />
              </HierarchyDropdown>
              {distributorFilter?.enabled && onLoadDistributorOptions &&  (
                <DistributorFilterNew
                distributorFilter={distributorFilter}
                  filters={filters}
                  optionsMap={optionsMap}
                  loadingMap={loadingMap}
                  onFilterChange={onFilterChange}
                  onMultiFilterChange={onMultiFilterChange}
                  onLoadOptions={onLoadDistributorOptions}
                  disabled={shouldDisableDistributorFilter}
                  disabledReason={distributorDisabledReason}
                  dropdownWidth={170}
                  enableSelectAll={true}
                  selectAllLabel="Select all distributors"
                  searchText={distributorFilter?.field ? searchTextMap[distributorFilter.field] : undefined}
                  onSearchChange={distributorFilter?.field && onFilterInputChange 
                    ? (value:any) => onFilterInputChange(distributorFilter!.field, value)
                    : undefined}
                />
              )}
            </Box>
          </Box>
     

        {/* {!salesEnabled && distributorFilter?.enabled && onLoadDistributorOptions  && (
          <DistributorFilter
            filters={filters}
            optionsMap={optionsMap}
            loadingMap={loadingMap}
            onFilterChange={onFilterChange}
            onMultiFilterChange={onMultiFilterChange}
            enableSelectAll={true}
            selectAllLabel="Select all distributors"
            onLoadOptions={onLoadDistributorOptions}
            disabled={shouldDisableDistributorFilter}
            disabledReason={distributorDisabledReason}
            dropdownWidth={170}
            searchText={distributorFilter?.field ? searchTextMap[distributorFilter.field] : undefined}
            onSearchChange={distributorFilter?.field && onFilterInputChange 
              ? (value) => onFilterInputChange(distributorFilter!.field, value)
              : undefined}
          />
        )} */}
        
      </Box>
    </Box>
  );
};

export default DistributorTopFilter;
